"use client";

import { blogService } from "@/lib/services";
import { BlogPost } from "@/lib/types";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";

// Skeleton Loader Component
function BlogCardSkeleton() {
	return (
		<div className="bg-white rounded-lg overflow-hidden shadow-lg animate-pulse">
			<div className="relative h-56 bg-gray-300" />
			<div className="p-6">
				<div className="h-6 bg-gray-300 rounded mb-2 w-3/4" />
				<div className="h-4 bg-gray-300 rounded mb-3 w-1/4" />
				<div className="h-4 bg-gray-300 rounded mb-2" />
				<div className="h-4 bg-gray-300 rounded mb-4 w-5/6" />
				<div className="h-4 bg-gray-300 rounded w-24" />
			</div>
		</div>
	);
}

// Empty State Component
function EmptyState() {
	return (
		<div className="col-span-2 text-center py-12">
			<div className="max-w-md mx-auto">
				<div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
					<svg
						className="w-12 h-12 text-gray-400"
						fill="none"
						viewBox="0 0 24 24"
						stroke="currentColor"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
							d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
						/>
					</svg>
				</div>
				<h3 className="text-xl font-bold text-gray-900 mb-2">
					No Blog Posts Yet
				</h3>
				<p className="text-gray-600">
					Check back later for updates and stories from RFFA.
				</p>
			</div>
		</div>
	);
}

// Error State Component
function ErrorState({ onRetry }: { onRetry: () => void }) {
	return (
		<div className="col-span-2 text-center py-12">
			<div className="max-w-md mx-auto">
				<div className="w-24 h-24 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
					<svg
						className="w-12 h-12 text-red-500"
						fill="none"
						viewBox="0 0 24 24"
						stroke="currentColor"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
							d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
						/>
					</svg>
				</div>
				<h3 className="text-xl font-bold text-gray-900 mb-2">
					Oops! Something went wrong
				</h3>
				<p className="text-gray-600 mb-4">
					We couldn&apos;t load the blog posts. Please try again.
				</p>
				<button
					onClick={onRetry}
					className="px-6 py-2 bg-primary hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
				>
					Try Again
				</button>
			</div>
		</div>
	);
}

export default function BlogGrid() {
	const [posts, setPosts] = useState<BlogPost[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);

	const fetchPosts = async () => {
		setLoading(true);
		setError(false);

		try {
			const response = await blogService.getAll(1, 10);

			if (response.success && response.data) {
				setPosts(response.data);
			} else {
				setError(true);
			}
		} catch (err) {
			console.error("Error fetching blog posts:", err);
			setError(true);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchPosts();
	}, []);

	// Loading State
	if (loading) {
		return (
			<div className="grid grid-cols-1 md:grid-cols-2 gap-8">
				{[1, 2, 3, 4].map((i) => (
					<BlogCardSkeleton key={i} />
				))}
			</div>
		);
	}

	// Error State
	if (error) {
		return (
			<div className="grid grid-cols-1 md:grid-cols-2 gap-8">
				<ErrorState onRetry={fetchPosts} />
			</div>
		);
	}

	// Empty State
	if (posts.length === 0) {
		return (
			<div className="grid grid-cols-1 md:grid-cols-2 gap-8">
				<EmptyState />
			</div>
		);
	}

	// Success State with Data
	return (
		<div className="grid grid-cols-1 md:grid-cols-2 gap-8">
			{posts.map((post) => (
				<article
					key={post.id}
					className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow"
				>
					{/* Image */}
					<div className="relative h-56">
						<Image
							src={post.featured_image || "/assets/black/1.jpg"}
							alt={post.title}
							fill
							className="object-cover"
						/>
					</div>

					{/* Content */}
					<div className="p-6">
						<h2 className="text-xl font-bold text-gray-900 mb-2 uppercase">
							{post.title}
						</h2>
						<p className="text-sm text-gray-500 mb-3">
							{post.published_at
								? new Date(post.published_at).toLocaleDateString("en-US", {
										year: "numeric",
										month: "long",
										day: "numeric",
								  })
								: "Date"}
						</p>
						<p className="text-gray-600 leading-relaxed mb-4 line-clamp-3">
							{post.excerpt}
						</p>
						<Link
							href={`/blog/${post.id}`}
							className="text-sm font-bold text-gray-900 uppercase hover:text-primary transition-colors"
						>
							READ MORE
						</Link>
					</div>
				</article>
			))}
		</div>
	);
}
